// Every admin-only / dev page in one list, so none of them lives only in
// someone's memory of a URL. The Admin page (the nav's Admin link, admins only)
// lays it out (pages/admin/AdminDirectory.tsx). Adding a dev route? Add it here.
//
// Grouped by what you're working on, not by where the code lives. A label says
// what you GET, the description what it's for, in a line. `path` starting with
// "#" is a tool further down the Admin page itself; a path starting with
// "http" is somewhere else entirely and opens in a new tab.
// Never shown to players: several of these name the bots, which are a secret.
import { LOAD_TESTS } from "core/src/ai/load-tests.js";
import { crowdLevelNumber } from "core/src/ai/crowd.js";

export interface AdminPageLink {
  path: string;
  label: string;
  desc: string;
}
export interface AdminPageGroup {
  id: string;
  title: string;
  about: string;
  icon: string;  // pages/setup/Icon name
  color: string; // the group's accent
  pages: AdminPageLink[];
}

// One load test per crowd level (core load-tests.js), each an unrated online
// match at that level's biggest roster with the netstats overlay open.
const LOAD_TEST_PAGES: AdminPageLink[] = LOAD_TESTS.map((t: { base: string; level: number; capacity: number }) => ({
  path: `/game/${t.base}?netstats=1`,
  label: t.capacity === 2 ? "Level " + crowdLevelNumber(t.level) + ": 1v1" : "Level " + crowdLevelNumber(t.level) + ": " + t.capacity + " players",
  desc: t.capacity === 2
    ? "You against one bot, where the bot may spend the most thinking"
    : "You and " + (t.capacity - 1) + " bots, the biggest match level " + crowdLevelNumber(t.level) + " has to carry",
}));

export const ADMIN_PAGES: AdminPageGroup[] = [
  {
    id: "bots",
    title: "Bots",
    about: "Measure them and tune them",
    icon: "sliders",
    color: "#ff9f45",
    pages: [
      { path: "/admin/bots", label: "Bot management", desc: "Every bot's rank and games per level; tune a bot's settings or the power-up values" },
      { path: "/debug/bots", label: "Bot trait lab", desc: "Play against bots and drag their traits while they drive" },
    ],
  },
  {
    id: "load",
    title: "Bot load tests",
    about: "Can the server keep pace? One per bot level, at its biggest match",
    icon: "gauge",
    color: "#ff5c5c",
    pages: LOAD_TEST_PAGES,
  },
  {
    id: "online",
    title: "Online and netcode",
    about: "The live match flow, lag and prediction",
    icon: "globe",
    color: "#28c4d4",
    pages: [
      { path: "/admin/stats", label: "Play stats", desc: "Who is playing right now, and games and players per day for each mode" },
      { path: "/admin/replays", label: "Replays", desc: "Every finished online match, replayable, with each death's cause and the dying player's own report" },
      { path: "/online", label: "Instant online match", desc: "Skip the lobby wait: a private match, right now. F9 shows network stats" },
      { path: "/online?netlag=160&netjitter=40&netstats=1", label: "Instant match on bad internet", desc: "160 ms ping with jitter, to feel what a laggy player feels" },
      { path: "/debug/net", label: "Netcode lab", desc: "Server and two clients side by side on a fake network; record and step frames" },
    ],
  },
  {
    id: "screens",
    title: "Game screens",
    about: "Jump to a screen without playing your way there",
    icon: "clipboard",
    color: "#b06cff",
    pages: [
      { path: "/debug/game", label: "In-game screen", desc: "The live game layout with a mock 12-player match (?players=N)" },
      { path: "/debug/round-over", label: "Round over screen", desc: "Between rounds, with real trails and scores" },
      { path: "/debug/match-over", label: "Match over screen", desc: "The final results and the winner's celebration" },
      { path: "/debug/matches", label: "Profile matches", desc: "The profile's Matches tab on fixture data: sessions, arena cards, every card state" },
      { path: "/debug/match-end", label: "Rating change modal", desc: "The end-of-match modal where your rating climbs" },
      { path: "/local?hints=1", label: "New-player hints", desc: "Local setup with the first-run hints forced on for this tab" },
      { path: "/debug/ranked-lobby", label: "Ranked start-page lab", desc: "The layouts and the search flow the ranked design came from, every state, desktop and phone" },
      { path: "/debug/open-achievements", label: "Open achievements lab", desc: "Made-up achievements waiting on the profile: open one card or all, the eggs fly into the count" },
      { path: "/debug/today-designs", label: "Today row lab", desc: "Ten designs for the worm level and the daily challenges on the start page, PC and phone, on the real page" },
      { path: "/start-designs", label: "Start page lab", desc: "Clickable start-page redesigns (plus the current page) phone and PC side by side; unlisted public link, safe to share" },
      { path: "/debug/sound-lab", label: "Sound lab", desc: "Every moment that makes or should make a sound, with numbered candidates to play side by side, the music candidates under an intensity slider, and a scripted round with the picks" },
      { path: "/debug/duel-board", label: "Duel scoreboard lab", desc: "Eight designs for the ranked 1v1 scoreboard under a wide board on a computer: the best-of marks and the player cards with flags" },
      { path: "/debug/duel-dots", label: "Duel score dots lab", desc: "Five designs for the duel HUD's First to 3 marks, desktop rail and phone readout" },
      { path: "/debug/duel-round-win", label: "Duel round win lab", desc: "Five animations for who took the round in a ranked 1v1, with match point, decider and match win, desktop and phone" },
      { path: "/debug/ffa-finding", label: "6 FFA search lab", desc: "Five sketches for the six-seat search (each seat finding its enemy), desktop and phone, and the banner at larger scales" },
      { path: "/debug/ffa-banner", label: "6 FFA banner lab", desc: "Ten designs for the six-player match-found grid, desktop and phone" },
      { path: "/ranked/duel?hold=1", label: "Ranked: finding a match, held", desc: "The real search screen (warm-up round + finding card), never connects, no sign-in needed" },
      { path: "/ranked/duel?hold=found", label: "Ranked: 1v1 match found, held", desc: "The real slab banner frozen on mock seats" },
      { path: "/ranked/ffa?hold=found", label: "Ranked: 6 FFA match found, held", desc: "The real starting grid frozen on mock seats" },
    ],
  },
  {
    id: "ranks",
    title: "Ranks",
    about: "The insignia ladder, everywhere it shows",
    icon: "trophy",
    color: "#f5c542",
    pages: [
      { path: "/debug/ranks", label: "Every rank insignia", desc: "The whole ladder on one sheet" },
      { path: "/?ranksdemo", label: "Ranks on your lobby row", desc: "Your row's badge cycles through the whole ladder" },
      { path: "/leaderboard?ranksdemo", label: "Ranks on the leaderboard", desc: "The same cycle, on the leaderboard" },
    ],
  },
  {
    id: "visuals",
    title: "Power-up and worm visuals",
    about: "Galleries of every look we tried; the first four can switch yours",
    icon: "bolt",
    color: "#7cfc66",
    pages: [
      { path: "/pu", label: "Power-up tokens", desc: "63 designs for the pickup you drive over. ?pu=N wears one in game" },
      { path: "/pa", label: "Power-up spawn and grab", desc: "How a token appears, and vanishes when taken. ?pa=N" },
      { path: "/fx", label: "Active effect rings", desc: "The status ring around your head while an effect runs. ?fx=N" },
      { path: "/wh", label: "Wall hack walls", desc: "What the arena border does while wall hack is on. ?wh=N" },
      { path: "/sw", label: "Switcharoonie swap", desc: "20 swap animations; the game runs #31" },
      { path: "/spawn", label: "Worm spawn-in", desc: "Nine spawn-in ideas. None shipped: the roll call took this spot" },
    ],
  },
  {
    id: "marketing",
    title: "Marketing",
    about: "What we hand out, and what we have promised ourselves to do next",
    icon: "info",
    color: "#4aa3ff",
    pages: [
      { path: "/debug/brand-lab", label: "Brand lab", desc: "Cover (3:4) and logo alternatives side by side; click one for full size" },
      { path: "/admin/marketing", label: "Marketing shelf", desc: "Screenshots worth sending, the brand files, and the copy that is already live" },
      { path: "http://localhost:5180/", label: "Growth board", desc: "The weekly growth backlog: SEO, communities, creators, people to talk to. Runs locally, `npm run board` in the growth-board repo" },
    ],
  },
  {
    id: "flags",
    title: "Flags and lobby layouts",
    about: "Cosmetics galleries and layout experiments",
    icon: "shop",
    color: "#ff2e88",
    pages: [
      { path: "#shop-test", label: "Shop test switches", desc: "Free test purchases, or equip anything unowned (on this page)" },
      { path: "/flagshop", label: "Shop flags gallery", desc: "Every purchasable novelty flag, as icon and as card" },
      { path: "/flagstyles", label: "Flag on the player row", desc: "Five ways to show your flag in your row" },
      { path: "/flagcard", label: "Flag as its own card", desc: "Variations where the flag fills a whole card" },
      { path: "/playerbar", label: "Player card + big flag", desc: "Start-page layouts for your card and flag" },
      { path: "/playground", label: "Custom lobby layout", desc: "The three-pane custom lobby design" },
    ],
  },
];
